/**
 * The design's icon set: inline stroke SVG, Lucide geometry, 2 px stroke,
 * `currentColor` (design-system readme "Icons"). Paths are copied verbatim
 * from the design markup.
 *
 * `react-native-svg` (13.14, already a dependency and already in jest's
 * transform allow-list) rather than @expo/vector-icons: the design's icons are
 * Lucide, the bundled MaterialCommunityIcons glyphs are not, and a font glyph
 * cannot take a 2 px stroke at 18 px. One consistent way, per the spec.
 */
import React from 'react';
import Svg, {Circle, Path} from 'react-native-svg';

export type IconName =
  | 'draw'
  | 'resign'
  | 'abort'
  | 'flip'
  | 'sound'
  | 'mute'
  | 'settings'
  | 'sun'
  | 'moon'
  | 'first'
  | 'prev'
  | 'next'
  | 'last'
  | 'review'
  | 'plus'
  | 'check'
  | 'alert'
  // fix D (R2): the rail's four action tiles
  | 'home'
  | 'bulb'
  | 'chart'
  | 'trophy'
  | 'close'
  | 'info'
  // fix Z2.3: the parental-controls CTA (rail/live/ActionBar)
  | 'shield';

const PATHS: {[K in IconName]: string[]} = {
  // "Offer draw" is the ½ glyph in the design, not an icon — see Toolbar.
  draw: ['M5 9h14M5 15h14'],
  resign: [
    'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z',
    'M4 22v-7',
  ],
  abort: ['m15 9-6 6', 'M9 9l6 6'],
  flip: ['m21 16-4 4-4-4M17 20V4M3 8l4-4 4 4M7 4v16'],
  sound: [
    'M11 5 6 9H2v6h4l5 4V5z',
    'M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14',
  ],
  mute: ['M11 5 6 9H2v6h4l5 4V5z', 'm22 9-6 6M16 9l6 6'],
  settings: [
    'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  ],
  // the theme tile / switch: "what you switch TO" (Lucide sun / moon)
  sun: [
    'M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41',
  ],
  moon: ['M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z'],
  first: ['m11 17-5-5 5-5M18 17l-5-5 5-5'],
  prev: ['m15 18-6-6 6-6'],
  next: ['m9 18 6-6-6-6'],
  last: ['m13 17 5-5-5-5M6 17l5-5-5-5'],
  review: ['M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z'],
  plus: ['M12 5v14M5 12h14'],
  check: ['M20 6 9 17l-5-5'],
  alert: [
    'M12 9v4M12 17h.01',
    'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  ],
  // Lucide `house`: "back to the lobby" (R2)
  home: [
    'M3 10.2 12 3l9 7.2V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z',
    'M9 21v-7h6v7',
  ],
  // Lucide `lightbulb`: the engine / self-analysis toggle (R2)
  bulb: [
    'M9 18h6M10 21h4',
    'M12 3a6 6 0 0 0-3.6 10.8c.6.5.9 1.1 1 1.8V18h5.2v-2.4c.1-.7.4-1.3 1-1.8A6 6 0 0 0 12 3z',
  ],
  // Lucide `bar-chart-3`: "open the full review" (R2)
  chart: ['M3 3v18h18', 'M8 17V10M13 17V6M18 17v-4'],
  // Lucide `trophy`: the tournament tab switch (R2)
  trophy: [
    'M8 21h8M12 17v4',
    'M7 4h10v5a5 5 0 0 1-10 0z',
    'M7 6H5a2 2 0 0 0 2 4M17 6h2a2 2 0 0 1-2 4',
  ],
  // the bare × of the analysis header's stop control
  close: ['M18 6 6 18M6 6l12 12'],
  info: ['M12 16v-5M12 8h.01'],
  // Lucide `shield`, no circle (fix Z2.3)
  shield: [
    'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
  ],
};

/** icons that also need a circle (Lucide draws them as circle + path) */
const CIRCLES: Partial<{[K in IconName]: [number, number, number]}> = {
  abort: [12, 12, 10],
  review: [12, 12, 3],
  sun: [12, 12, 4],
  info: [12, 12, 10],
};

type IconProps = {name: IconName; size?: number; color: string};

function Icon({name, size = 18, color}: IconProps) {
  const circle = CIRCLES[name];
  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round">
      {circle && (
        <Circle cx={circle[0]} cy={circle[1]} r={circle[2]} stroke={color} />
      )}
      {PATHS[name].map(d => (
        <Path key={d} d={d} stroke={color} />
      ))}
    </Svg>
  );
}

export default React.memo(Icon);
